import { SelectionTool, Tool, Web3DViewer } from '@technology/web3d';
import {
  CloudMarkupTool,
  ArrowMarkupTool,
  LineMarkupTool,
  TextMarkupTool,
  FreelineMarkupTool,
} from '@technology/web3d-plugin-markup';
import { detectArrayChanges } from '@trimble/utilities';
import { MarkupsCapabilityProvider } from '@trimble/web3d-providers';
import { createEffect, createRoot, on } from 'solid-js';
import {
  createDrawCreationGateEffect,
  enableMarkupToolOnViewer,
  restoreSelectionOnViewerIfAllowed,
  type MarkupPerViewerState,
} from '../helpers/markup/markup-draw-creation-gate';
import { SharedCapabilityBase } from './base/shared-capability-base';
import { CapabilityCategory } from './base/models/capability-category';

const MARKUP_TOOL_CLASSES = [
  CloudMarkupTool,
  ArrowMarkupTool,
  LineMarkupTool,
  TextMarkupTool,
  FreelineMarkupTool,
] as const;

/**
 * Handles interactive creation of annotation markups (cloud, arrow, line, text, draw)
 * across all registered viewers. Tools remain active after placing a markup — the user
 * can keep placing markups until they explicitly deactivate via the toolbar toggle.
 *
 * Draw/freeline creation is gated per viewer via
 * {@link Web3DViewerWrapper.isDrawMarkupCreationAllowed}; display/replication is not.
 *
 * All sync/replication logic lives in MarkupsCapabilityProvider.
 * This capability manages only tool activation and multi-tap coordination.
 */
export class MarkupCapability extends SharedCapabilityBase {
  public override readonly category: CapabilityCategory =
    CapabilityCategory.Markup;

  private readonly perViewerState = new Map<
    Web3DViewer,
    MarkupPerViewerState
  >();
  private activeToolName: string | undefined;
  private effectDispose?: () => void;
  private drawCreationGateDispose?: () => void;

  /**
   * Image/panorama point-cloud setup briefly switches `viewer.activeTool` (e.g. to
   * clipBox). That can leave markup tap tools subscribed against a stale cursor
   * pipeline. Re-applying the active markup tool after active-tool changes fixes
   * placement without toggling the toolbar off/on.
   */
  private readonly onActiveToolChanged = (event: Event): void => {
    queueMicrotask(() => {
      if (!this.isActive() || !this.activeToolName) {
        return;
      }
      const target = event.target;
      if (!target || !this.perViewerState.has(target as Web3DViewer)) {
        return;
      }
      this.enableToolOnViewer(target as Web3DViewer, this.activeToolName);
    });
  };

  constructor(markupsProvider: MarkupsCapabilityProvider) {
    super(markupsProvider);

    // Wire multi-tap coordination callbacks into the provider.
    markupsProvider.setMultiTapCoordinator({
      disableToolOnOtherViewers: (sourceViewer: Web3DViewer) => {
        this.disableToolOnOtherViewers(sourceViewer);
      },
      enableToolOnAllViewers: (excludeViewer: Web3DViewer) => {
        if (!this.activeToolName) return;
        for (const [viewer] of this.perViewerState) {
          if (viewer === excludeViewer) continue;
          this.enableToolOnViewer(viewer, this.activeToolName);
        }
      },
      ensureActiveMarkupToolOnViewer: (viewer: Web3DViewer) => {
        if (!this.isActive() || !this.activeToolName) {
          return;
        }
        this.enableToolOnViewer(viewer, this.activeToolName);
      },
    });

    this.effectDispose = createRoot((dispose) => {
      createEffect(
        on(this.viewerWrappers, (current, previous) => {
          const { newItems, removedItems } = detectArrayChanges(
            previous,
            current
          );

          for (const removed of removedItems) {
            this.teardownViewer(removed.viewer);
          }

          for (const added of newItems) {
            this.setupViewer(added.viewer);
          }
        })
      );
      return dispose;
    });
  }

  protected override trackIsEnabled(): void {
    this.enable();
  }

  public override deactivate(): boolean {
    // Viewer cleanup must finish before super.deactivate() flips isActive —
    // Solid effects subscribed to the falling edge (e.g. Model3DToolBar's
    // resting-state bridge) run synchronously on that signal write.
    if (this.isActive() && this.isEnabled()) {
      this.disposeDrawCreationGate();
      this.disableActiveToolOnAllViewers();
    }
    return super.deactivate();
  }

  public override cleanup(): Promise<void> {
    this.disposeDrawCreationGate();
    this.disableActiveToolOnAllViewers();
    this.effectDispose?.();
    this.effectDispose = undefined;

    for (const viewer of [...this.perViewerState.keys()]) {
      this.teardownViewer(viewer);
    }

    return Promise.resolve();
  }

  private setupViewer(viewer: Web3DViewer): void {
    if (this.perViewerState.has(viewer)) {
      return;
    }

    const selectionTool = viewer.tools.get(SelectionTool)!;
    const markupTools = new Map<string, Tool>();
    for (const toolClass of MARKUP_TOOL_CLASSES) {
      const tool = viewer.tools.get(
        toolClass as unknown as new (...params: unknown[]) => Tool
      );
      if (tool) {
        markupTools.set(toolClass.Name, tool);
      }
    }

    this.perViewerState.set(viewer, { selectionTool, markupTools });

    this.addViewerEventListener(
      viewer,
      'active-tool-changed',
      this.onActiveToolChanged as EventListener
    );

    if (this.isActive() && this.activeToolName) {
      this.enableToolOnViewer(viewer, this.activeToolName);
    }
  }

  private teardownViewer(viewer: Web3DViewer): void {
    const state = this.perViewerState.get(viewer);
    if (!state) {
      return;
    }

    if (this.activeToolName) {
      const tool = state.markupTools.get(this.activeToolName);
      if (tool) {
        tool.enabled = false;
      }
      restoreSelectionOnViewerIfAllowed(
        viewer,
        this.activeToolName,
        state,
        () => this.viewerWrappers()
      );
    }

    this.removeAllViewerEventListeners(viewer);
    this.perViewerState.delete(viewer);
  }

  private enableToolOnViewer(viewer: Web3DViewer, toolName: string): void {
    const state = this.perViewerState.get(viewer);
    if (!state) {
      return;
    }

    enableMarkupToolOnViewer(viewer, toolName, state, () =>
      this.viewerWrappers()
    );
  }

  private disableToolOnOtherViewers(sourceViewer: Web3DViewer): void {
    if (!this.activeToolName) return;
    for (const [viewer, state] of this.perViewerState) {
      if (viewer === sourceViewer) continue;
      const tool = state.markupTools.get(this.activeToolName);
      if (tool) {
        tool.enabled = false;
      }
      restoreSelectionOnViewerIfAllowed(
        viewer,
        this.activeToolName,
        state,
        () => this.viewerWrappers()
      );
    }
  }

  private disableActiveToolOnAllViewers(): void {
    const deactivatedToolName = this.activeToolName;
    this.activeToolName = undefined;
    for (const [viewer, state] of this.perViewerState) {
      for (const [, tool] of state.markupTools) {
        tool.enabled = false;
      }
      if (deactivatedToolName) {
        restoreSelectionOnViewerIfAllowed(
          viewer,
          deactivatedToolName,
          state,
          () => this.viewerWrappers()
        );
      }
    }
  }

  private disposeDrawCreationGate(): void {
    this.drawCreationGateDispose?.();
    this.drawCreationGateDispose = undefined;
  }

  private startDrawCreationGateEffect(): void {
    this.disposeDrawCreationGate();
    this.drawCreationGateDispose = createDrawCreationGateEffect({
      getViewerWrappers: () => this.viewerWrappers(),
      getPerViewerState: () => this.perViewerState,
      getActiveToolName: () => this.activeToolName,
    });
  }

  private setActiveTool(toolName: string): void {
    if (this.activeToolName) {
      for (const [, state] of this.perViewerState) {
        const prevTool = state.markupTools.get(this.activeToolName);
        if (prevTool) {
          prevTool.enabled = false;
        }
      }
    }

    this.activeToolName = toolName;
    this.activate();
    this.disposeDrawCreationGate();

    for (const [viewer] of this.perViewerState) {
      this.enableToolOnViewer(viewer, toolName);
    }

    if (toolName === FreelineMarkupTool.Name) {
      this.startDrawCreationGateEffect();
    }
  }

  public startCloudMarkupCreation(): void {
    this.setActiveTool(CloudMarkupTool.Name);
  }

  public startArrowMarkupCreation(): void {
    this.setActiveTool(ArrowMarkupTool.Name);
  }

  public startLineMarkupCreation(): void {
    this.setActiveTool(LineMarkupTool.Name);
  }

  public startTextMarkupCreation(): void {
    this.setActiveTool(TextMarkupTool.Name);
  }

  public startDrawMarkupCreation(): void {
    this.setActiveTool(FreelineMarkupTool.Name);
  }
}
